/**
 * /help — Help & Shortcuts.
 *
 * One-stop landing page for new users:
 *   - Live module documentation index (fetched from /api/help/list)
 *   - Keyboard shortcuts table (sourced live from the shortcut registry,
 *     so it stays in sync with whatever each page registers)
 *   - Quick "how to" cards covering the most-asked workflows
 *   - "Try it" buttons that navigate to the relevant page
 */
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import DescriptionIcon from "@mui/icons-material/Description";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

import { api } from "@/lib/api";
import {
  useRegisteredShortcuts,
  type ShortcutScope,
} from "@/lib/shortcutRegistry";

interface HelpDocSummary {
  path: string;
  folder: string;
  file: string;
  title: string;
  audience: string | null;
  priority: string;
}

interface HelpFolderSummary {
  slug: string;
  title: string;
  description: string;
  doc_count: number;
  priority?: string;
  docs: HelpDocSummary[];
}

export const Route = createFileRoute("/_auth/help")({
  component: HelpPage,
});

const SCOPE_ORDER: ShortcutScope[] = ["Global", "Navigation", "Page", "Form"];

interface Guide {
  title: string;
  steps: string[];
  tryTo?: string;
  tryLabel?: string;
}

const GUIDES: Guide[] = [
  {
    title: "Migrating from your old system",
    steps: [
      "Setup → Configuration → Data Migration & Setup",
      "Choose 'Set up a new company' for a first-time cutover",
      "Drop four exports in order: chart of accounts → open AR → open AP → trial balance",
      "Each step previews before it writes; missing customers/vendors are auto-created",
      "Finish on Reconcile — Opening Balance Equity should read $0 (ties out)",
    ],
    tryTo: "/data-migration",
    tryLabel: "Open Data Migration",
  },
  {
    title: "Entering an expense",
    steps: [
      "Go to Purchases → Expenses",
      "Pick 'Snap or upload receipt' — or 'Enter manually' for no AI",
      "Review the pre-filled form (✨ chips show AI suggestions)",
      "Save as draft, or Submit for approval",
    ],
    tryTo: "/expenses",
    tryLabel: "Open Expenses",
  },
  {
    title: "Reconciling a bank statement",
    steps: [
      "Accounting → Bank Reconciliation → New",
      "Pick the bank account + statement period",
      "Match journals to statement lines (auto-suggest covers most)",
      "Resolve differences and finalize",
    ],
    tryTo: "/bank-rec",
    tryLabel: "Open Bank Rec",
  },
  {
    title: "Approving invoices",
    steps: [
      "Approvals tab in the header (badge = how many waiting on you)",
      "Pick an item → AI summary explains what changed and why",
      "Approve, request changes, or reject with a comment",
    ],
    tryTo: "/approvals",
    tryLabel: "Open Approvals",
  },
  {
    title: "Closing the period",
    steps: [
      "Accounting → Periodic → Fiscal Periods",
      "Run the close checklist (recurring journals, FX reval, recognition)",
      "Lock the period — entries can't be posted to it after close",
    ],
    tryTo: "/periods",
    tryLabel: "Open Periods",
  },
  {
    title: "Running payroll",
    steps: [
      "Payroll → Pay Runs → New",
      "Pick the pay period and employees",
      "Calculate gross + taxes, review preview, then post to GL",
    ],
    tryTo: "/payroll/pay-runs",
    tryLabel: "Open Pay Runs",
  },
  {
    title: "Inviting a reviewer or team member",
    steps: [
      "Setup → Team → Create an invitation",
      "Choose Private demo reviewer for isolated dummy data, or Team member for company access",
      "Add their name and email, choose the starting sample or company role, and send",
      "After sending, copy the personal follow-up so they know exactly where to begin",
    ],
    tryTo: "/settings/team",
    tryLabel: "Open Invitations",
  },
];

function HelpPage() {
  const navigate = useNavigate();
  const all = useRegisteredShortcuts();

  const folders = useQuery({
    queryKey: ["help-list"],
    queryFn: async () => (await api("/api/help/list")) as HelpFolderSummary[],
  });

  const grouped = useMemo(() => {
    const buckets = new Map<ShortcutScope, typeof all>();
    for (const s of all) {
      const arr = buckets.get(s.scope) ?? [];
      arr.push(s);
      buckets.set(s.scope, arr);
    }
    for (const [scope, arr] of buckets) {
      const seen = new Set<string>();
      buckets.set(
        scope,
        arr.filter((s) => {
          const key = `${s.combo}|${s.label}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        }),
      );
    }
    return buckets;
  }, [all]);

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: "auto" }}>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 0.5 }}>
        Help & Shortcuts
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Press <kbd style={kbdStyle}>?</kbd> anywhere to see the shortcut sheet.
      </Typography>

      {/* ── Agentic Accounting intro ── */}
      <AgenticAccountingSection
        onOpenAgent={(prompt?) =>
          window.dispatchEvent(
            new CustomEvent("bynaus:open-agent", { detail: { prompt } }),
          )
        }
        onChat={() => navigate({ to: "/chat" as any })}
      />

      {/* ── Module documentation (live from docs/help/) ── */}
      <Typography variant="overline" color="text.secondary">
        Module documentation
      </Typography>
      {folders.isLoading ? (
        <Box sx={{ py: 2 }}><CircularProgress size={20} /></Box>
      ) : folders.data && folders.data.length > 0 ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
            gap: 2,
            mt: 1,
            mb: 4,
          }}
        >
          {folders.data
            .filter((f) => f.slug !== "_root")
            .map((f) => {
              const readme = f.docs.find((d) => d.file.toLowerCase() === "readme.md") ?? f.docs[0];
              const otherDocs = f.docs.filter((d) => d !== readme).slice(0, 4);
              return (
                <Card key={f.slug} variant="outlined">
                  <CardActionArea
                    component={Link}
                    to={`/help/docs/${readme.path.replace(/^docs\/help\//, "")}` as any}
                  >
                    <CardContent>
                      <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                        <MenuBookIcon color="primary" fontSize="small" />
                        <Typography variant="subtitle2" fontWeight={600}>
                          {f.title}
                        </Typography>
                        <Chip size="small" label={`${f.doc_count} docs`} variant="outlined" />
                      </Stack>
                      {f.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {f.description}
                        </Typography>
                      )}
                      {otherDocs.length > 0 && (
                        <Stack spacing={0.25} mt={1}>
                          {otherDocs.map((d) => (
                            <Typography key={d.path} variant="caption" color="text.secondary">
                              · {d.title}
                            </Typography>
                          ))}
                          {f.doc_count > otherDocs.length + 1 && (
                            <Typography variant="caption" color="text.disabled">
                              + {f.doc_count - otherDocs.length - 1} more
                            </Typography>
                          )}
                        </Stack>
                      )}
                    </CardContent>
                  </CardActionArea>
                </Card>
              );
            })}
        </Box>
      ) : (
        <Box sx={{ py: 2 }}>
          <Typography variant="body2" color="text.secondary">
            No module docs found at docs/help/.
          </Typography>
        </Box>
      )}

      {/* ── Module guides ── */}
      <Typography variant="overline" color="text.secondary">
        Quick guides
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
          gap: 2,
          mt: 1,
          mb: 4,
        }}
      >
        {GUIDES.map((g) => (
          <Card key={g.title} variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                {g.title}
              </Typography>
              <Box component="ol" sx={{ pl: 2.5, m: 0 }}>
                {g.steps.map((s, i) => (
                  <Typography
                    key={i}
                    component="li"
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 0.5 }}
                  >
                    {s}
                  </Typography>
                ))}
              </Box>
            </CardContent>
            {g.tryTo && (
              <CardActions>
                <Button
                  size="small"
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  onClick={() => navigate({ to: g.tryTo as any })}
                >
                  {g.tryLabel ?? "Try it"}
                </Button>
              </CardActions>
            )}
          </Card>
        ))}
      </Box>

      {/* ── Shortcuts ── */}
      <Typography variant="overline" color="text.secondary">
        Keyboard shortcuts
      </Typography>
      <Stack spacing={2} sx={{ mt: 1 }}>
        {SCOPE_ORDER.filter((sc) => (grouped.get(sc)?.length ?? 0) > 0).map((scope) => {
          const rows = grouped.get(scope) ?? [];
          return (
            <Paper key={scope} variant="outlined">
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  bgcolor: "action.hover",
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight={600}
                  sx={{ textTransform: "uppercase", letterSpacing: "0.04em" }}
                >
                  {scope}
                </Typography>
              </Box>
              <Box>
                {rows.map((s) => (
                  <Box
                    key={s.id}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      px: 2,
                      py: 1,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      "&:last-child": { borderBottom: 0 },
                    }}
                  >
                    <Typography variant="body2">{s.label}</Typography>
                    <ComboKeys combo={s.combo} />
                  </Box>
                ))}
              </Box>
            </Paper>
          );
        })}
        {all.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No shortcuts registered yet — open another page first.
          </Typography>
        )}
      </Stack>

      <Box sx={{ mt: 4, color: "text.secondary" }}>
        <Typography variant="caption">
          Need more? Try the AI assistant — press{" "}
          <kbd style={kbdStyle}>⌘</kbd> <kbd style={kbdStyle}>J</kbd> or visit{" "}
          <Link to="/chat">Chat</Link>.
        </Typography>
      </Box>
    </Box>
  );
}

const kbdStyle: React.CSSProperties = {
  fontFamily: "monospace",
  fontSize: 11,
  padding: "1px 6px",
  borderRadius: 4,
  border: "1px solid #cbd5e1",
  background: "#f8fafc",
  color: "#334155",
};

function ComboKeys({ combo }: { combo: string }) {
  const isSequence = /\s/.test(combo) && !combo.includes("+");
  const tokens = isSequence
    ? combo.split(/\s+/)
    : combo.split("+").map((t) => t.trim());
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      {tokens.map((t, i) => (
        <Stack key={i} direction="row" spacing={0.5} alignItems="center">
          {i > 0 && (
            <Typography variant="caption" color="text.disabled">
              {isSequence ? "then" : "+"}
            </Typography>
          )}
          <Chip
            label={prettyKey(t)}
            size="small"
            variant="outlined"
            sx={{ fontFamily: "monospace", fontSize: 11, height: 22 }}
          />
        </Stack>
      ))}
    </Stack>
  );
}

function prettyKey(k: string): string {
  const lower = k.toLowerCase();
  if (lower === "cmd" || lower === "meta") return "⌘";
  if (lower === "ctrl") return "Ctrl";
  if (lower === "shift") return "⇧";
  if (lower === "alt" || lower === "option") return "⌥";
  return k.length === 1 ? k.toUpperCase() : k;
}

// ─── Agentic Accounting introduction ─────────────────────────────────────────

const EXAMPLE_PROMPTS = [
  "What's blocking the period from closing?",
  "Show me unpaid AP invoices over 30 days",
  "Set up equipment costing for my fleet",
  "Why was invoice INV-2031 held?",
  "Reverse posting J-2026-0431 — payroll was wrong",
  "Explain my AR aging in plain English",
];

const CAN_DO = [
  { icon: "🔍", label: "Read your data",      detail: "GL, AP/AR, projects, payroll runs, vendors, equipment, approvals" },
  { icon: "📝", label: "Take action",         detail: "Post invoices, reverse journals, close periods, create cost codes — always with confirmation" },
  { icon: "🧠", label: "Explain decisions",   detail: "Why a posting was held, why a period won't close, what variance means" },
  { icon: "⚙️", label: "Set up modules",       detail: "Conversational onboarding for equipment, payroll, vendor compliance — no UI navigation needed" },
];

const SAFETY = [
  "Read-only by default — actions require explicit confirmation",
  "Honors your role + permissions — can't bypass approval policies",
  "Every AI action is logged with full audit trail",
  "Won't post to a closed period or override compliance gates",
];

function AgenticAccountingSection({
  onOpenAgent,
  onChat,
}: {
  onOpenAgent: (prompt?: string) => void;
  onChat: () => void;
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 4,
        p: 3,
        borderColor: "primary.main",
        borderWidth: 1,
        background: (t) =>
          t.palette.mode === "dark"
            ? "linear-gradient(135deg, rgba(37,99,235,0.08), rgba(37,99,235,0.02))"
            : "linear-gradient(135deg, #EFF6FF, #FFFFFF)",
      }}
    >
      {/* Header */}
      <Stack direction="row" spacing={1.5} alignItems="center" mb={1}>
        <AutoAwesomeIcon color="primary" />
        <Typography variant="h6" fontWeight={700}>
          Agentic Accounting
        </Typography>
        <Chip size="small" label="Unique to Bynaus" color="primary" variant="outlined" />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Ledger Core ships with <strong>Ask Bynaus</strong> — an AI agent that knows your books,
        reads your data, explains the accounting state, and takes action on your behalf.
        Press <kbd style={kbdStyle}>⌘</kbd> <kbd style={kbdStyle}>J</kbd> from anywhere to open it.
      </Typography>

      {/* CTAs */}
      <Stack direction="row" spacing={1} mb={3} flexWrap="wrap" useFlexGap>
        <Button
          variant="contained"
          size="small"
          startIcon={<AutoAwesomeIcon />}
          onClick={onOpenAgent}
        >
          Open Ask Bynaus
        </Button>
        <Button variant="outlined" size="small" onClick={onChat}>
          Open full chat
        </Button>
      </Stack>

      {/* What it can do */}
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ display: "block", mb: 1 }}
      >
        What the agent can do
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: 1.5,
          mb: 3,
        }}
      >
        {CAN_DO.map((c) => (
          <Box
            key={c.label}
            sx={{
              p: 1.5,
              borderRadius: 1,
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
              <Typography sx={{ fontSize: 18 }}>{c.icon}</Typography>
              <Typography variant="subtitle2" fontWeight={600}>
                {c.label}
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {c.detail}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Try saying */}
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ display: "block", mb: 1 }}
      >
        Try saying
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
        {EXAMPLE_PROMPTS.map((p) => (
          <Chip
            key={p}
            label={`"${p}"`}
            variant="outlined"
            size="small"
            onClick={() => onOpenAgent(p)}
            sx={{ cursor: "pointer" }}
          />
        ))}
      </Stack>

      {/* Safety / what to expect */}
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ display: "block", mb: 1 }}
      >
        What to expect
      </Typography>
      <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
        {SAFETY.map((s) => (
          <Typography
            key={s}
            component="li"
            variant="body2"
            color="text.secondary"
            sx={{ mb: 0.25 }}
          >
            {s}
          </Typography>
        ))}
      </Box>
    </Paper>
  );
}
